"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

import { Button } from "@/components/ui/button";
import { ArrowIcon, GearIcon, InfoIcon, ClockIcon } from "@/icons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { WalletConnectButton } from "@/components/connect-button";
import { useToast } from "@/hooks/use-toast";
import {
  useBridge,
  useBridgeTransaction,
  type BridgeDirection,
} from "@/hooks/useBridge";
import {
  useCampUSDNucleus,
  useCampUSDTransaction,
} from "@/hooks/useCampUSDNucleus";

import {
  BridgeTokenSelector,
  getDefaultBridgeToken,
  getAvailableTokens,
  type BridgeTokenConfig,
} from "./bridge-token-selector";
import { FeeDisplay } from "./fee-display";
import { TokenConfig } from "@/lib/bridge/tokens";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { useAccount } from "wagmi";

export function BridgeLayout() {
  const { address, chain, isConnected } = useAccount();
  const { toast } = useToast();
  // Initialize direction based on token - USDC and ETH default to camp-to-eth (one-way)
  const [direction, setDirection] = useState<BridgeDirection>("camp-to-eth");
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState("0.5");
  const [selectedToken, setSelectedToken] = useState<BridgeTokenConfig>(
    getDefaultBridgeToken(direction),
  );

  // When token or direction changes, ensure one-way tokens can't bridge in
  useEffect(() => {
    const isOneWay = selectedToken.id === "usdc" || selectedToken.id === "eth";
    if (isOneWay && direction === "eth-to-camp") {
      // If a one-way token is selected and direction is eth-to-camp, switch to camp-to-eth
      setDirection("camp-to-eth");
    }
  }, [selectedToken.id, direction]);

  // When direction changes to eth-to-camp, switch to CAMP if a one-way token is selected
  useEffect(() => {
    const isOneWay = selectedToken.id === "usdc" || selectedToken.id === "eth";
    if (direction === "eth-to-camp" && isOneWay) {
      // Switch to CAMP token since USDC and ETH are not available for eth-to-camp
      const campToken = getAvailableTokens("eth-to-camp")[0];
      if (campToken) {
        setSelectedToken(campToken);
      }
    }
  }, [direction, selectedToken.id]);
  const currentToastRef = useRef<{ id: string; dismiss: () => void } | null>(
    null,
  );

  // Convert BridgeTokenConfig to TokenConfig for compatibility
  const tokenConfigForBridge: TokenConfig = {
    id: selectedToken.id,
    symbol: selectedToken.symbol,
    name: selectedToken.name,
    description: `${selectedToken.name} - Multi-chain token`,
    icon: selectedToken.icon,
    decimals: selectedToken.decimals,
    isNative:
      direction === "eth-to-camp"
        ? selectedToken.ethereum.isNative
        : selectedToken.camp.isNative,
    contractAddress:
      direction === "eth-to-camp"
        ? selectedToken.ethereum.contractAddress
        : selectedToken.camp.contractAddress,
  };

  const {
    bridgeState,
    getBalanceFormatted,
    getBalance,
    getBalanceSymbol,
    bridge,
    canBridge,
    getRequiredChain,
    isOnCorrectChain,
    switchChain,
  } = useBridge(tokenConfigForBridge);

  const { isConfirming, isConfirmed } = useBridgeTransaction(
    bridgeState.txHash,
  );

  // campUSD Nucleus hooks
  const {
    operationState: campUSDState,
    depositCampUSD,
    withdrawCampUSD,
    canDeposit,
    canWithdraw,
  } = useCampUSDNucleus();

  const { isConfirming: isCampUSDConfirming, isConfirmed: isCampUSDConfirmed } =
    useCampUSDTransaction(campUSDState.txHash);

  const balance = getBalanceFormatted(direction, tokenConfigForBridge);
  const rawBalance = getBalance(direction, tokenConfigForBridge);
  const balanceSymbol = getBalanceSymbol(direction, tokenConfigForBridge);
  const requiredChain = getRequiredChain(direction);

  // Check if current token is one-way only (USDC and ETH can only bridge out from Camp)
  const isOneWayToken = selectedToken.id === "usdc" || selectedToken.id === "eth";
  const isOneWayDirectionBlocked = isOneWayToken && direction === "eth-to-camp";

  const handleDirectionSwap = () => {
    // Prevent swapping to eth-to-camp for one-way tokens (USDC and ETH)
    if (isOneWayToken && direction === "camp-to-eth") {
      return; // Don't allow swap for one-way tokens
    }
    
    const newDirection =
      direction === "camp-to-eth" ? "eth-to-camp" : "camp-to-eth";
    setDirection(newDirection);
    // Keep the same token when swapping direction - it will show the appropriate network version
    setAmount("");
  };

  const handleTokenSelect = (token: BridgeTokenConfig | null) => {
    if (token) {
      setSelectedToken(token);
      // If selecting a one-way token (USDC or ETH), ensure direction is camp-to-eth
      if (token.id === "usdc" || token.id === "eth") {
        setDirection("camp-to-eth");
      }
    }
  };

  const handleMaxClick = () => {
    setAmount(rawBalance);
  };

  const handleSwitchChain = async () => {
    try {
      // Always try the native switch first; if the connector is ready this succeeds
      await switchChain({ chainId: requiredChain.id });
      return;
    } catch (error) {
      // If the connector isn't ready after refresh, log the error
      console.warn("Chain switch failed:", error);
    }
  };

  const handleBridge = async () => {
    if (!amount || !address) return;

    // Block bridging in (eth-to-camp) for one-way tokens (USDC and ETH)
    if (isOneWayDirectionBlocked) {
      toast({
        variant: "destructive",
        title: "❌ Bridge Direction Not Allowed",
        description: `${selectedToken.symbol} can only be bridged from Camp Network to Ethereum (one-way only).`,
      });
      return;
    }

    try {
      // Handle campUSD transactions using Nucleus SDK
      if (selectedToken.id === "campusd") {
        if (direction === "eth-to-camp") {
          // Deposit USDC and bridge to campUSD on Camp Network
          if (!canDeposit(amount)) return;

          await depositCampUSD({
            depositAmount: amount,
            slippage: parseFloat(slippage) * 100, // Convert to basis points
          });
        } else {
          // Withdraw campUSD and bridge back to USDC on Ethereum
          if (!canWithdraw(amount)) return;

          await withdrawCampUSD({
            withdrawAmount: amount,
            slippage: parseFloat(slippage) * 100, // Convert to basis points
          });
        }
      } else {
        // Use existing bridge logic for other tokens
        if (!canBridge(direction, amount, tokenConfigForBridge)) return;

        await bridge(direction, {
          to: address,
          amount: amount,
          slippage: BigInt(Math.floor(parseFloat(slippage) * 100)),
          token: tokenConfigForBridge,
        });
      }
    } catch (error) {
      console.error("Bridge transaction failed:", error);
    }
  };

  const isValidAmount =
    selectedToken.id === "campusd"
      ? direction === "eth-to-camp"
        ? canDeposit(amount)
        : canWithdraw(amount)
      : canBridge(direction, amount, tokenConfigForBridge);
  // More intelligent chain switching logic
  const hasWagmiChain = !!chain;
  const needsChainSwitch =
    isConnected && hasWagmiChain && !isOnCorrectChain(direction);

  // Flag for when we're in fallback mode (connected but no chain info)
  const isInFallbackMode = isConnected && !hasWagmiChain;

  // Handle transaction status changes with toast notifications
  useEffect(() => {
    if (currentToastRef.current) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
    }

    if (bridgeState.isLoading) {
      currentToastRef.current = toast({
        title: "⏳ Wallet Confirmation Required",
        description: "Please confirm the transaction in your wallet.",
        variant: "default",
      });
    } else if (bridgeState.isError && bridgeState.error) {
      const errorMessage = bridgeState.error.message.toLowerCase();
      const isUserRejection =
        errorMessage.includes("user rejected") ||
        errorMessage.includes("user denied") ||
        errorMessage.includes("user cancelled") ||
        errorMessage.includes("cancelled") ||
        errorMessage.includes("rejected");

      if (isUserRejection) {
        currentToastRef.current = toast({
          variant: "default",
          title: "❌ Transaction Cancelled",
          description: "You cancelled the transaction.",
        });
      } else {
        currentToastRef.current = toast({
          variant: "destructive",
          title: "❌ Transaction Failed",
          description: bridgeState.error.message,
        });
      }
    } else if (bridgeState.isSuccess && bridgeState.txHash && !isConfirmed) {
      currentToastRef.current = toast({
        title: "🚀 Transaction Sent!",
        description: `Your bridge transaction has been submitted to the network. Hash: ${bridgeState.txHash.slice(0, 10)}...`,
        variant: "default",
      });
    }
  }, [
    bridgeState.isLoading,
    bridgeState.isError,
    bridgeState.isSuccess,
    bridgeState.txHash,
    bridgeState.error,
    isConfirmed,
    toast,
  ]);

  // Handle transaction confirmation
  useEffect(() => {
    if (isConfirmed && bridgeState.txHash) {
      if (currentToastRef.current) {
        currentToastRef.current.dismiss();
        currentToastRef.current = null;
      }

      toast({
        title: "✅ Transaction Confirmed!",
        description:
          "Your bridge request has been processed and will appear in your activity tab shortly.",
        variant: "default",
      });

      setAmount("");
    }
  }, [isConfirmed, bridgeState.txHash, toast]);

  // Handle campUSD transaction status changes
  useEffect(() => {
    if (selectedToken.id !== "campusd") return;

    if (currentToastRef.current) {
      currentToastRef.current.dismiss();
      currentToastRef.current = null;
    }

    if (campUSDState.isLoading) {
      const stepMessage = {
        approving: "Please approve USDC spending in your wallet.",
        depositing: "Please confirm the campUSD deposit transaction.",
        withdrawing: "Please confirm the campUSD withdrawal transaction.",
        completed: "Transaction completed!",
      }[campUSDState.step || "approving"];

      currentToastRef.current = toast({
        title: "⏳ Wallet Confirmation Required",
        description: stepMessage,
        variant: "default",
      });
    } else if (campUSDState.isError && campUSDState.error) {
      const errorMessage = campUSDState.error.message.toLowerCase();
      const isUserRejection =
        errorMessage.includes("user rejected") ||
        errorMessage.includes("user denied") ||
        errorMessage.includes("user cancelled") ||
        errorMessage.includes("cancelled") ||
        errorMessage.includes("rejected");

      if (isUserRejection) {
        currentToastRef.current = toast({
          variant: "default",
          title: "❌ Transaction Cancelled",
          description: "You cancelled the campUSD transaction.",
        });
      } else {
        currentToastRef.current = toast({
          variant: "destructive",
          title: "❌ campUSD Transaction Failed",
          description: campUSDState.error.message,
        });
      }
    } else if (
      campUSDState.isSuccess &&
      campUSDState.txHash &&
      !isCampUSDConfirmed
    ) {
      currentToastRef.current = toast({
        title: "🚀 Transaction Sent!",
        description: `Your campUSD transaction has been submitted. Hash: ${campUSDState.txHash.slice(0, 10)}...`,
        variant: "default",
      });
    }
  }, [
    selectedToken.id,
    campUSDState.isLoading,
    campUSDState.isError,
    campUSDState.isSuccess,
    campUSDState.txHash,
    campUSDState.error,
    campUSDState.step,
    isCampUSDConfirmed,
    toast,
  ]);

  // Handle campUSD transaction confirmation
  useEffect(() => {
    if (selectedToken.id !== "campusd") return;

    if (isCampUSDConfirmed && campUSDState.txHash) {
      if (currentToastRef.current) {
        currentToastRef.current.dismiss();
        currentToastRef.current = null;
      }

      toast({
        title: "✅ campUSD Transaction Confirmed!",
        description: `Your campUSD ${direction === "eth-to-camp" ? "deposit" : "withdrawal"} has been processed and will appear in your activity tab shortly.`,
        variant: "default",
      });

      setAmount("");
    }
  }, [
    selectedToken.id,
    direction,
    isCampUSDConfirmed,
    campUSDState.txHash,
    toast,
  ]);

  // Cleanup toast on unmount
  useEffect(() => {
    return () => {
      if (currentToastRef.current) {
        currentToastRef.current.dismiss();
      }
    };
  }, []);

  const getButtonText = () => {
    if (!isConnected) return "Connect Wallet to Bridge";
    if (needsChainSwitch) return `Switch to ${requiredChain.name}`;
    if (isInFallbackMode) return `Connect to ${requiredChain.name} & Bridge`;

    // Handle campUSD specific text
    if (selectedToken.id === "campusd") {
      if (campUSDState.isLoading) {
        switch (campUSDState.step) {
          case "approving":
            return "Approve USDC in Wallet...";
          case "depositing":
            return "Confirm campUSD Deposit...";
          case "withdrawing":
            return "Confirm campUSD Withdrawal...";
          default:
            return "Confirm in Wallet...";
        }
      }
      if (isCampUSDConfirming) return "Processing Transaction...";
      if (!amount) return "Enter Amount";
      if (!isValidAmount) return "Insufficient Balance";

      const action = direction === "eth-to-camp" ? "Deposit" : "Withdraw";
      const token = direction === "eth-to-camp" ? "USDC" : "campUSD";
      return `${action} ${amount} ${token}`;
    }

    // Handle LayerZero bridge operations (ETH/CAMP)
    const layerZeroBridgeState = bridgeState as typeof bridgeState & {
      approvalStep?: "checking" | "approving" | "approved" | "bridging";
    };

    if (bridgeState.isApproving || layerZeroBridgeState.approvalStep) {
      const approvalStep = layerZeroBridgeState.approvalStep;
      switch (approvalStep) {
        case "checking":
          return "Checking Token Allowance...";
        case "approving":
          return "Approve Token in Wallet...";
        case "approved":
          return "Token Approved, Preparing Bridge...";
        case "bridging":
          return "Confirm Bridge in Wallet...";
        default:
          if (bridgeState.isApproving) return "Approving Token...";
      }
    }

    if (bridgeState.isLoading) return "Confirm in Wallet...";
    if (isConfirming) return "Processing Transaction...";
    if (!amount) return "Enter Amount";
    if (!isValidAmount) return "Insufficient Balance";

    // Show provider info in button text
    // const providerText = isPaxosBridge ? "(via Paxos)" : "(via LayerZero)";
    return `Bridge ${amount} ${balanceSymbol}`;
  };

  const getButtonLoadingState = () => {
    if (selectedToken.id === "campusd") {
      return campUSDState.isLoading || isCampUSDConfirming;
    }

    const layerZeroBridgeState = bridgeState as typeof bridgeState & {
      approvalStep?: "checking" | "approving" | "approved" | "bridging";
    };

    return (
      bridgeState.isLoading ||
      isConfirming ||
      bridgeState.isApproving ||
      !!layerZeroBridgeState.approvalStep
    );
  };

  const getButtonAction = () => {
    if (needsChainSwitch) return handleSwitchChain;
    if (isInFallbackMode) return handleSwitchChain; // Try to connect to proper chain first
    return handleBridge;
  };

  const isButtonDisabled =
    isConnected &&
    !needsChainSwitch &&
    !isInFallbackMode &&
    (!isValidAmount || getButtonLoadingState() || isOneWayDirectionBlocked);

  const getTokenDescription = () => {
    if (!selectedToken) return "";

    switch (selectedToken.id) {
      case "usdc":
        if (direction === "eth-to-camp") {
          return "USDC bridging from Ethereum to Camp Network is not available. USDC can only be bridged from Camp Network to Ethereum (one-way only).";
        }
        return "Bridge USDC from Camp Network to Ethereum (one-way only).";
      case "camp":
        return direction === "eth-to-camp"
          ? "Bridge CAMP tokens from Ethereum to Camp Network where they become the native gas token."
          : "Bridge native CAMP tokens from Camp Network back to Ethereum as ERC20 tokens.";
      case "eth":
        if (direction === "eth-to-camp") {
          return "ETH bridging from Ethereum to Camp Network is not available. ETH can only be bridged from Camp Network to Ethereum (one-way only).";
        }
        return "Bridge ETH from Camp Network to Ethereum (one-way only).";
      default:
        return "Bridge your tokens between Ethereum and Camp Network.";
    }
  };

  // Show loading overlay when wallet providers are syncing
  const isLoading = false; // Can add loading state if needed
  if (isLoading) {
    return (
      <div className="relative flex w-full flex-col gap-3 rounded-sm border border-[#261000]/15 bg-[#FDFDFC] p-6 pt-0 max-sm:p-4 max-sm:pt-0">
        {/* Loading overlay */}
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-sm bg-[#FDFDFC]/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#FF6D01] border-t-transparent"></div>
            <p className="text-sm text-[#838383]">Syncing wallet...</p>
          </div>
        </div>
        {/* Placeholder content */}
        <div className="mt-3.5 h-[400px] opacity-50">
          <div className="space-y-4">
            <div className="h-16 w-full animate-pulse rounded bg-gray-200"></div>
            <div className="h-16 w-full animate-pulse rounded bg-gray-200"></div>
            <div className="h-12 w-full animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3 rounded-sm border border-[#261000]/15 bg-[#FDFDFC] p-6 pt-0 max-sm:p-4 max-sm:pt-0">
      {/* USDC OFT Sunset Announcement */}
      <Alert className="mt-3.5 border-[#FF6D01]/30 bg-[#FFF4E6]">
        <AlertCircle className="text-[#FF6D01]" />
        <AlertDescription className="flex flex-col gap-2 text-[#1A1A1A]">
          <span className="text-xs font-medium sm:text-sm">
            <strong>USDC & ETH OFTs is being sunsetted.</strong> We&apos;re transitioning to{" "}
            <a
              href="https://stargate.finance/?dstChain=camp&dstToken=0x8a2B28364102Bea189D99A475C494330Ef2bDD0B"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#FF6D01] underline hover:text-[#FF5101]">
              USDC.E by Stargate
            </a>
            {" "}for improved fungibility and liquidity.{" "}
            <a
              href="https://docs.campnetwork.xyz/smart-contracts/canonical-assets"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#FF6D01] underline hover:text-[#FF5101]">
              View supported assets on Camp Network
            </a>
            .
          </span>
        </AlertDescription>
      </Alert>

      {/* Settings Gear */}
      <div className="flex w-full flex-row items-center justify-end">
        <div className="flex cursor-pointer items-center">
          <Popover>
            <PopoverTrigger>
              <GearIcon size={14} />
            </PopoverTrigger>
            <PopoverContent
              align="end"
              side="top"
              className="rounded-sm border-[#261000]/15 bg-white p-1">
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="ml-2 flex flex-row items-center gap-2">
                  <p className="text-xs font-medium text-[#1A1A1A]">
                    Slippage Tolerance
                  </p>
                  <div>
                    <Tooltip>
                      <TooltipTrigger>
                        <InfoIcon />
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="center"
                        sideOffset={4}
                        className="border border-[#261000]/15 bg-white">
                        <p className="flex flex-col gap-1 text-xs font-normal -tracking-[0.33px] text-[#1A1A1A]">
                          <span>
                            Your transaction will revert if the price changes
                          </span>
                          <span>more than the slippage percentage</span>
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                <ToggleGroup
                  type="single"
                  value={slippage}
                  onValueChange={(value) => {
                    if (value) {
                      setSlippage(value);
                    }
                  }}
                  className="">
                  <ToggleGroupItem value="0.5">
                    <p className="text-sm font-medium text-[#1A1A1A]">0.5%</p>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="1">
                    <p className="text-sm font-medium text-[#1A1A1A]">1%</p>
                  </ToggleGroupItem>
                  <ToggleGroupItem value="2">
                    <p className="text-sm font-medium text-[#1A1A1A]">2%</p>
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="relative flex w-full flex-col gap-3">
        <div className="relative z-1 flex flex-col">
          {/* From Token Section */}
          <div className="flex flex-row items-center justify-between gap-4 rounded-xs bg-[#F9F7F5] px-6 py-8 max-sm:flex-col max-sm:items-stretch max-sm:gap-4 max-sm:px-4 max-sm:py-6">
            <div className="flex flex-col items-start gap-0.5 max-sm:order-1 max-sm:w-full">
              <div className="flex items-center gap-2 max-sm:justify-start">
                <p className="font-sans-serif text-base font-medium">From</p>
                <span
                  className={cn(
                    "-ml-3 scale-75 rounded-full px-2 py-1 text-xs font-medium max-sm:ml-0 max-sm:scale-100",
                    direction === "eth-to-camp"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-orange-100 text-orange-800",
                  )}>
                  {direction === "eth-to-camp" ? "Ethereum" : "Camp Network"}
                </span>
              </div>
              <Input
                type="number"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={getButtonLoadingState()}
                className="font-sans-serif rounded-none border-none py-6 pl-0 text-left text-black shadow-none outline-none max-sm:py-4 max-sm:text-lg"
              />
              {selectedToken && isConnected && (
                <div className="flex items-center gap-2 text-left text-xs text-gray-400">
                  <span>Balance: {balance}</span>
                  {parseFloat(balance) > 0 && (
                    <button
                      onClick={handleMaxClick}
                      disabled={getButtonLoadingState()}
                      className="text-xs font-medium text-[#FF6D01] uppercase hover:text-[#FF5101] disabled:opacity-50">
                      MAX
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="max-sm:order-2">
              <BridgeTokenSelector
                selectedToken={selectedToken}
                onTokenSelect={handleTokenSelect}
                label="From"
                direction={direction}
              />
            </div>
          </div>

          {/* Token Interchange Button */}
          <div className="pointer-events-none absolute inset-x-0 -bottom-7.5 flex flex-row items-center justify-between max-sm:relative max-sm:inset-x-auto max-sm:bottom-0 max-sm:my-2">
            <div className="max-sm:hidden" />
            <div className="pointer-events-auto rounded-xs bg-[#FDFDFC] p-1.5 max-sm:mx-auto">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      size="icon"
                      onClick={handleDirectionSwap}
                      disabled={getButtonLoadingState() || (isOneWayToken && direction === "camp-to-eth")}
                      className="size-8 cursor-pointer rounded-xs bg-[#FF6D01] shadow-none hover:bg-[#FF6D01]/90 disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg
                        width="16"
                        height="12"
                        viewBox="0 0 16 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M15.6567 7.45386C15.8687 7.66596 15.8688 8.00938 15.6567 8.22144L12.3413 11.5369L11.9575 11.9216L11.5737 11.5369L8.2573 8.22144C8.04567 8.00952 8.04588 7.66591 8.2573 7.45386C8.46939 7.24176 8.81376 7.24176 9.02586 7.45386L11.4145 9.84253L11.4145 1.20581C11.4146 0.905972 11.6577 0.662965 11.9575 0.662842C12.2574 0.662842 12.5004 0.905896 12.5005 1.20581L12.5005 9.84253L14.8891 7.45386C15.1012 7.24176 15.4446 7.24177 15.6567 7.45386Z"
                          fill="white"
                        />
                        <path
                          d="M7.93968 5.13062C8.15154 4.91851 8.15169 4.57505 7.93968 4.36304L4.62425 1.04761L4.24046 0.662842L3.85667 1.04761L0.540266 4.36304C0.328653 4.57497 0.328816 4.91858 0.540266 5.13061C0.752356 5.34271 1.09673 5.34271 1.30882 5.13061L3.69749 2.74194L3.69749 11.3787C3.69753 11.6786 3.94054 11.9216 4.24046 11.9216C4.54029 11.9215 4.78339 11.6785 4.78343 11.3787L4.78343 2.74194L7.1721 5.13062C7.38419 5.34271 7.72759 5.34271 7.93968 5.13062Z"
                          fill="white"
                        />
                      </svg>
                    </Button>
                  </div>
                </TooltipTrigger>
                {isOneWayToken && direction === "camp-to-eth" && (
                  <TooltipContent
                    side="bottom"
                    align="center"
                    sideOffset={4}
                    className="border border-[#261000]/15 bg-white">
                    <p className="text-xs font-normal -tracking-[0.33px] text-[#1A1A1A]">
                      {selectedToken.symbol} can only be bridged from Camp Network to Ethereum (one-way only)
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
            <div className="max-sm:hidden" />
          </div>
        </div>

        {/* To Token Section */}
        <div className="z-0 flex flex-row items-center justify-between rounded-xs bg-[#F9F7F5] px-6 py-8 max-sm:flex-col max-sm:items-stretch max-sm:gap-4 max-sm:px-4 max-sm:py-6">
          <div className="flex flex-col items-start gap-0.5 max-sm:order-1 max-sm:w-full">
            <div className="flex items-center gap-2 max-sm:justify-start">
              <p className="font-sans-serif text-base font-medium">To</p>
              <span
                className={cn(
                  "-ml-3 scale-75 rounded-full px-2 py-1 text-xs font-medium max-sm:ml-0 max-sm:scale-100",
                  direction !== "eth-to-camp"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-orange-100 text-orange-800",
                )}>
                {direction !== "eth-to-camp" ? "Ethereum" : "Camp Network"}
              </span>
            </div>
            <Input
              type="number"
              placeholder="0.0"
              value={amount}
              readOnly
              className="font-sans-serif rounded-none border-none bg-transparent py-6 pl-0 text-left text-black shadow-none outline-none max-sm:py-4 max-sm:text-lg"
            />
            {selectedToken && isConnected && (
              <span className="text-xs text-gray-400 max-sm:self-start">
                Balance:{" "}
                {getBalanceFormatted(
                  direction === "camp-to-eth" ? "eth-to-camp" : "camp-to-eth",
                  tokenConfigForBridge,
                )}
              </span>
            )}
          </div>
          <div className="max-sm:order-2">
            <BridgeTokenSelector
              selectedToken={selectedToken}
              onTokenSelect={handleTokenSelect}
              label="To"
              direction={
                direction === "camp-to-eth" ? "eth-to-camp" : "camp-to-eth"
              }
            />
          </div>
        </div>

        {/* LayerZero Fee Display - Only for Camp to Ethereum */}
        <FeeDisplay
          amount={amount}
          tokenId={selectedToken.id}
          direction={direction}
        />
        {direction === "camp-to-eth" && selectedToken.symbol === "CAMP" && (
          <div className="flex flex-row items-center gap-2">
            <ClockIcon color="#FF4506" />
            <span className="font-sans-serif text-xs font-medium text-[#FF4506]">
              7 Day hold required
            </span>
          </div>
        )}

        {/* Bridge Button */}
        <div className="mt-2 flex flex-col">
          {!isConnected ? (
            <WalletConnectButton WalletConnectName="Connect Wallet" />
          ) : (
            <Button
              onClick={getButtonAction()}
              disabled={isButtonDisabled}
              className="relative cursor-pointer gap-3 rounded-none bg-[#FF6D01] px-4 py-3 font-mono text-xs font-semibold text-white uppercase hover:bg-[#FF5101] disabled:cursor-not-allowed disabled:opacity-50">
              {getButtonText()}
              {!getButtonLoadingState() && <ArrowIcon />}
              <span className="absolute top-1 left-1 aspect-square size-1 bg-white/30" />
              <span className="absolute top-1 right-1 aspect-square size-1 bg-white/30" />
              <span className="absolute bottom-1 left-1 aspect-square size-1 bg-white/30" />
              <span className="absolute right-1 bottom-1 aspect-square size-1 bg-white/30" />
            </Button>
          )}
        </div>

        {/* Token Information Panel */}
        {selectedToken && (
          <div className="mt-4 rounded-sm border border-[#261000]/10 bg-[#FDFDFC] p-4">
            <div className="flex items-start gap-3">
              <Image
                src={selectedToken.icon}
                alt={selectedToken.symbol}
                width={24}
                height={24}
                className="mt-0.5 size-6 rounded-full"
              />
              <div className="flex-1">
                <h3 className="font-sans-serif text-sm font-semibold text-[#1A1A1A]">
                  {selectedToken.name}
                </h3>
                <p className="font-sans-serif mt-1 text-xs text-[#868686]">
                  {getTokenDescription()}
                </p>
                {selectedToken.id === "campusd" && (
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-xs font-medium text-[#FF6D01]">
                      Powered by nucleusearn.io
                    </span>
                    <a
                      href="https://nucleusearn.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#FF6D01] hover:underline">
                      ↗
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-col">
          <p className="font-sans-serif text-center text-base font-semibold text-[#868686] max-sm:text-sm">
            Powered By LayerZero
          </p>
        </div>
      </div>
    </div>
  );
}
